import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Loader2, CheckCircle, AlertCircle, Sparkles, Image as ImageIcon } from 'lucide-react';
import { aiAPI } from '../services/api';
import toast from 'react-hot-toast';

const AISinistreModal = ({ open, onClose, onScanned }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);

    const onDrop = (acceptedFiles) => {
        setFiles(prev => [...prev, ...acceptedFiles]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        maxFiles: 5
    });

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleAnalyze = async () => {
        if (files.length === 0) {
            toast.error('Veuillez ajouter au moins un document (Mandat ou Carte Grise)');
            return;
        }

        setLoading(true);
        setScanning(true);

        const formData = new FormData();
        files.forEach(file => {
            formData.append('documents', file);
        });

        try {
            const res = await aiAPI.extractSinistreData(formData);
            if (res.data.success) {
                toast.success('Documents analysés avec succès !');
                onScanned(res.data.data); // Envoyer les données au parent
                onClose();
            } else {
                throw new Error(res.data.message || 'Erreur d\'analyse');
            }
        } catch (error) {
            console.error('AI Analysis error:', error);
            toast.error(error.response?.data?.message || 'L\'IA n\'a pas pu lire les documents. Veuillez réessayer.');
        } finally {
            setLoading(false);
            setScanning(false);
        }
    };

    if (!open) return null;

    return (
        <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: '600px', overflow: 'hidden' }}>
                <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--primary-900), var(--primary-800))', color: 'white', border: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #a855f7, #6366f1)', padding: '8px', borderRadius: '10px' }}>
                            <Sparkles size={20} color="white" />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>Nouveau Sinistré par IA</h3>
                            <p style={{ margin: 0, fontSize: '11px', opacity: 0.7 }}>Scannez vos documents pour remplir le formulaire automatiquement</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="modal-close" style={{ color: 'white', background: 'rgba(255,255,255,0.1)' }}>
                        <X size={18} />
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '24px' }}>
                    {!scanning ? (
                        <>
                            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`} style={{
                                border: '2px dashed var(--primary-200)',
                                borderRadius: '16px',
                                background: isDragActive ? 'var(--primary-50)' : 'var(--surface-2)',
                                padding: '40px 20px',
                                textAlign: 'center',
                                transition: 'all 0.2s ease'
                            }}>
                                <input {...getInputProps()} />
                                <div style={{ background: 'white', width: '56px', height: '56px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                                    <Upload size={24} color="var(--primary-500)" />
                                </div>
                                <h4 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '8px' }}>Déposez le Mandat et la Carte Grise</h4>
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Glissez-déposez vos photos ou cliquez pour parcourir</p>
                            </div>

                            {files.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <h5 style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '10px' }}>Documents à scanner ({files.length})</h5>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {files.map((file, index) => (
                                            <div key={index} style={{
                                                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
                                                background: 'var(--surface-2)', borderRadius: '10px', border: '1px solid var(--border)'
                                            }}>
                                                <ImageIcon size={16} color="var(--primary-400)" />
                                                <span style={{ flex: 1, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</span>
                                                <button onClick={() => removeFile(index)} style={{ color: 'var(--danger)', background: 'none', border: 'none', padding: '4px' }}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 20px' }}>
                                <Loader2 className="spinner" size={80} style={{ color: 'var(--primary-500)', opacity: 0.2 }} />
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Sparkles size={32} className="ia-pulse-icon" style={{ color: '#a855f7' }} />
                                </div>
                            </div>
                            <h4 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '8px' }}>Analyse Par IA...</h4>
                            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Lecture des documents et extraction des données techniques en cours.</p>
                            
                            <div style={{ 
                                maxWidth: '200px', height: '4px', background: 'var(--border)', 
                                borderRadius: '2px', margin: '20px auto 0', overflow: 'hidden' 
                            }}>
                                <div className="loading-bar-ia" style={{ height: '100%', width: '30%', background: 'linear-gradient(to right, #a855f7, #6366f1)', borderRadius: '2px' }} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer" style={{ padding: '16px 24px', background: 'var(--surface-2)' }}>
                    <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Annuler</button>
                    {!scanning && (
                        <button 
                            className="btn btn-primary" 
                            style={{ 
                                background: 'linear-gradient(135deg, #a855f7, #6366f1)', 
                                boxShadow: '0 4px 15px rgba(168, 85, 247, 0.4)',
                                border: 'none'
                             }}
                            onClick={handleAnalyze} 
                            disabled={files.length === 0 || loading}
                        >
                            {loading ? <Loader2 size={16} className="spinner" /> : <Sparkles size={16} />}
                            Démarrer l'extraction IA
                        </button>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes ia-pulse-icon {
                    0% { transform: scale(1); opacity: 0.8; }
                    50% { transform: scale(1.2); opacity: 1; }
                    100% { transform: scale(1); opacity: 0.8; }
                }
                .ia-pulse-icon {
                    animation: ia-pulse-icon 1.5s infinite ease-in-out;
                }
                .loading-bar-ia {
                    animation: loading-bar-move 2s infinite linear;
                }
                @keyframes loading-bar-move {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(333%); }
                }
            `}</style>
        </div>
    );
};

export default AISinistreModal;
